import type { JSX } from "react";
import { useEffect, useRef, useState } from "react";
import { IconBell } from "../components/icons";
import { hasUnread, visibleEntries, displayDate, recordPublished, type WhatsNewEntry } from "./entries";
import { ReleaseNoteModal } from "./ReleaseNoteModal";

/**
 * Top-bar 新功能 (What's New) entry — openspec/cyclecare-whats-new.md, design
 * 方案 A. A bell button with an unread dot; clicking opens a dropdown panel of
 * recent release notes. Clicking an entry opens its full release note (with the
 * demo video) in a modal. "Unread" is purely local: the newest entry id is
 * compared against a localStorage marker, and opening the panel marks it read.
 * Visible to all roles. Renders nothing when there are no entries.
 */

const SEEN_KEY = "cyclecare.whatsnew.seen";

/** The bell is a "recent updates" feed, not a full archive — cap the list so it
 *  stays short as releases accumulate (older entries simply drop off the panel;
 *  the unread marker still tracks the very newest). */
const BELL_MAX = 3;

/** Read the last-seen entry id; tolerate a disabled/throwing localStorage. */
function readSeen(): string {
  try {
    return localStorage.getItem(SEEN_KEY) ?? "";
  } catch {
    return "";
  }
}

export function WhatsNew({ features }: { features?: Record<string, boolean> }): JSX.Element | null {
  // Entries gated on a runtime flag (`entry.flag`, a /healthz `features` key) show
  // only once that flag is on — a release is committed but published by flipping
  // the prod flag, no second code change. Un-flagged entries always show.
  const visible = visibleEntries(features);
  // Hooks must run unconditionally (rules-of-hooks); the empty-list guard is
  // below them. newestId is "" when there are no entries — unused in that case.
  const newestId = visible[0]?.id ?? "";

  const [open, setOpen] = useState(false);
  const [seen, setSeen] = useState<string>(readSeen);
  const [selected, setSelected] = useState<WhatsNewEntry | null>(null);
  const wrapRef = useRef<HTMLDivElement>(null);

  // Capture each flag-gated release's real go-live date the first time it's
  // visible (= the flag flip), so the bell shows when it actually shipped, not the
  // authoring/doc date. Stamped synchronously at mount (correct first paint) and
  // again if a flag flips mid-session.
  const today = new Date().toISOString().slice(0, 10);
  const [published, setPublished] = useState<Record<string, string>>(() => recordPublished(visible, today));
  const visibleKey = visible.map((e) => e.id).join(",");
  useEffect(() => {
    setPublished((cur) => recordPublished(visible, today, cur));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleKey, today]);

  // Close the panel on Escape or a click outside the bell + panel.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") setOpen(false);
    };
    const onClick = (e: MouseEvent): void => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    window.addEventListener("mousedown", onClick);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("mousedown", onClick);
    };
  }, [open]);

  if (visible.length === 0) return null;

  const unread = hasUnread(seen, visible);

  // Opening the panel marks the newest entry as read (dot clears, persisted).
  const openPanel = (): void => {
    setOpen(true);
    try {
      localStorage.setItem(SEEN_KEY, newestId);
    } catch {
      /* localStorage unavailable — dot stays, not fatal */
    }
    setSeen(newestId);
  };

  const toggle = (): void => (open ? setOpen(false) : openPanel());

  return (
    <div className="wn-wrap" ref={wrapRef}>
      <button
        className="icon-btn wn-btn"
        type="button"
        onClick={toggle}
        aria-label="新功能"
        aria-expanded={open}
      >
        <IconBell size={16} />
        {unread && <span className="wn-dot" aria-hidden="true" />}
      </button>

      {open && (
        <div className="wn-panel" role="dialog" aria-label="新功能">
          <div className="wn-head">
            <span className="wn-title">新功能</span>
          </div>
          <div className="wn-list">
            {visible.slice(0, BELL_MAX).map((e) => (
              <button
                className="wn-item"
                key={e.id}
                type="button"
                onClick={() => {
                  setSelected(e);
                  setOpen(false);
                }}
              >
                <span className="wn-when">{displayDate(e, published)}</span>
                <span className="wn-h">{e.title}</span>
                <span className="wn-d">{e.summary}</span>
                <span className="wn-more">
                  阅读全文<span aria-hidden="true"> →</span>
                </span>
              </button>
            ))}
          </div>
        </div>
      )}

      {selected && (
        <ReleaseNoteModal
          entry={selected}
          displayDate={displayDate(selected, published)}
          onClose={() => setSelected(null)}
        />
      )}
    </div>
  );
}
